import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Disclaimer from '../../others/Disclaimer';
import FareCalendar from '../../controls/TripFareCalendar/FareCalendar';
import MonthNavigation from '../../controls/TripFareCalendar/atoms/MonthNavigation';
import DayComponentInstance from '../../items/DayCardMobile/DayCalendarCompact';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import Skeleton from '../../others/Skeleton/SkeletonFareCalendarOI/SkeletonFareCalendarOI';
import useFiltersDisableSettings from '../InteractiveMapContainer/filters/useFiltersDisabled';
import SecondaryFilters from '../../controls/SecondaryFilters/SecondaryFilters';
//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getHistogramsSmart } from '../../../_integrations/everymundo/histogramSmart';
import { getAirports } from '../../../_integrations/everymundo/airports';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFilters';
import { getResultsLength } from '../_commons/calendar';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';
import { generateSecondaryFiltersSettings } from '../_commons/generateSecondaryFiltersSettings';

function LowFareCalendarOIV2Container(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    fareCalendar,
    legend,
    direction,
    initialHistograms,
    getAirports: getAirportsFn,
    getHistogramsSmart: getHistogramsFn,
    fareItemOnClickAction,
    showDisclaimer,
    showFilters,
    showSwitch,
    showTravelClassFilter,
    showSkeletonScreen,
    showLegend,
    skeletonSettings,
    travelClass: travelClassSettings,
    travelClassOptions,
    disclaimerContentSettings,
    mainContentSettings,
    legendContentSettings,
    legendRowSettings,
    filterContentSettings,
    minInitialResults,
    theme,
    filters,
    showSecondaryFilters = true,
    secondaryFilters,
    thresholdDay,
    // deprecated props
    origin,
    destination,
    disclaimer,
    switchOriginDestination,
    originDestinationLogic,
    enableSEO = true,
    defaultMonth = 0,
    datalayerTracking = false,
    numberOfMonthsToDisplay = 2
  } = props;
  const [showThresholdDay, setThresholdDay] = useState(!!thresholdDay);
  const [displayErrorView, setDisplayErrorView] = useState(false);
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);
  const combinable = props?.fareCalendar?.combinable ?? false;
  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    availableFilters: showTravelClassFilter
      ? ['OriginDestination', 'TravelClass']
      : ['OriginDestination'],
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        column: {
          justifyContent: 'center'
        },
        origin,
        destination,
        switch: switchOriginDestination
      },
      showOrigin: true,
      showDestination: true,
      showSwitch
    },
    travelClassSettings: {
      ...travelClassSettings,
      ...travelClassOptions
    },
    clearButtonDisplay: 'hidden',
    ...filters
  };

  const initialMonth = dayjs().add(prePopulation.dateRange.start, 'day').toDate();

  const thresholdDayEnable = Boolean(
    thresholdDay &&
      showThresholdDay &&
      dayjs(initialMonth).endOf('month').diff(dayjs(initialMonth), 'day') + 1 < thresholdDay
  );

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      pageContext,
      getAirports: getAirportsFn,
      maxItemsAmount: numberOfMonthsToDisplay || (defaultMonth ? defaultMonth : 1),
      nextMonth: numberOfMonthsToDisplay ? false : thresholdDayEnable || !!defaultMonth
    },
    initialData: initialHistograms,
    getFares: getHistogramsFn,
    faresTransformationLogic: 'histogram',
    minInitialResults,
    showNoResultsPlaceholder: false,
    availableFilters: showFilters ? filtersConfig.availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filtersConfig.hideBudgetOnMultiCurrency,
    routeMode: true,
    getResultsLength,
    showOrigin:
      (filtersConfig.originDestinationSettings &&
        filtersConfig.originDestinationSettings.showOrigin) ||
      secondaryFilters?.originDestinationSettings?.showOrigin,
    showDestination:
      (filtersConfig.originDestinationSettings &&
        filtersConfig.originDestinationSettings.showDestination) ||
      secondaryFilters?.originDestinationSettings?.showDestination,
    enabledhistogram: true,
    datalayerTracking,
    combinable,
    ignoreCurrencyCode: true,
    isMobile: false
  });

  useEffect(() => {
    dispatch({
      type: 'DATE_RANGE_HISTOGRAM_TYPE_CHANGE',
      payload: {
        histogramType: 'OUTBOUND'
      }
    });
    dispatch({
      type: 'MODIFY_ACTION_HISTOGRAM',
      payload: {
        action: '-'
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOriginSelected = Boolean(filtersState.locations.origin.value);
  const isDestinationSelected = Boolean(filtersState.locations.destination.value);
  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...props.secondaryFilters },
    {
      flightType: displayErrorView,
      travelClass: displayErrorView,
      redemptionUnit: displayErrorView,
      travelerInterest: displayErrorView,
      flightStopCount: displayErrorView,
      budget: displayErrorView
    }
  );

  const { dateRange } = filtersState;

  const dayCard = useFareItemProps(fareCalendar.dayCard, dispatch, fareItemOnClickAction);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  const changeMonth = (type, payload) => {
    dispatch({ type: type, payload: payload });
    setThresholdDay(false);
  };

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  let newDaySettings = { ...dayCard };
  const { showPriceOrMilesAtom = true } = dayCard;
  let newSecondaryFilters = { ...secondaryFilters };
  let newFilters = { ...filtersConfig };
  let newFareCalendar = {
    ...fareCalendar,
    legendRowSettings,
    legendContentSettings,
    legend,
    showLegend
  };

  if (!showPriceOrMilesAtom) {
    newDaySettings = {
      ...newDaySettings,
      showMiles: false,
      showPrice: false
    };
  } else {
    if (
      filtersState.redemptionUnit.value === 'POINTS' ||
      filtersState.redemptionUnit.value === 'MILES'
    ) {
      newDaySettings = {
        ...newDaySettings,
        showPrice: false,
        showMiles: true,
        dayCardMiles: {
          ...newDaySettings?.dayCardMiles,
          milesSymbol: filtersState.redemptionUnit.value.toLowerCase()
        }
      };
    } else {
      newDaySettings = {
        ...newDaySettings,
        showMiles: false,
        showPrice: true
      };
    }
  }

  if (
    filtersState.redemptionUnit.value === 'POINTS' ||
    filtersState.redemptionUnit.value === 'MILES'
  ) {
    newFareCalendar = {
      ...newFareCalendar,
      fareItemAtomSettings: {
        ...newFareCalendar?.fareItemAtomSettings,
        showPrice: false,
        showMiles: true,
        fareItemMiles: {
          ...newFareCalendar?.fareItemAtomSettings?.fareItemMiles,
          milesSymbol: filtersState.redemptionUnit.value.toLowerCase()
        }
      }
    };
    newSecondaryFilters = generateSecondaryFiltersSettings(
      filtersState.redemptionUnit.value,
      newSecondaryFilters
    );
    newFilters = {
      ...newFilters,
      budgetSettings: {
        ...newFilters?.budgetSettings,
        currencyToDisplay: filtersState.redemptionUnit.value
      }
    };
  } else {
    newFareCalendar = {
      ...newFareCalendar,
      fareItemAtomSettings: {
        ...newFareCalendar?.fareItemAtomSettings,
        showMiles: false,
        showPrice: true
      }
    };
  }

  const renderSecondaryFilters = showSecondaryFilters && (
    <SecondaryFilters
      includeAllFilters
      {...filters}
      {...filtersState}
      {...newSecondaryFilters}
      {...secondaryFiltersDisableSettings}
      filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
    />
  );

  const renderFareCalendar = ({ data, initialView, errorStatus = false }) => (
    <FareCalendar
      {...newFareCalendar}
      thresholdDayEnable={thresholdDayEnable}
      thresholdDay={thresholdDay}
      defaultMonth={defaultMonth}
      dayCard={newDaySettings}
      initialMonth={initialMonth}
      currentMonth={dateRange.value.start}
      endDateMonth={dateRange.value.end}
      data={data}
      isMobile={false}
      initialView={initialView && !match('LOADING')}
      NavComponentInstance={MonthNavigation}
      onSelectMonth={changeMonth}
      loading={match('LOADING')}
      DayComponentInstance={DayComponentInstance}
      filtersState={filtersState}
      globalDispatch={dispatch}
      errorStatus={errorStatus}
      displayErrorView={displayErrorView}
      setDisplayErrorView={setDisplayErrorView}
      combinable={combinable}
      tenantCode={prePopulation?.tenantCode}
    />
  );

  return (
    <Box
      position="relative"
      className={`${className} ${LowFareCalendarOIV2Container.componentName}`}
      style={myStyle}
      dataTestId="lowFareCalendarOIV2Container"
    >
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer
        defaultTitleValue={'LOWEST DAILY FARES FOR THE UPCOMING 90 DAYS'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
      {renderSecondaryFilters}
      {showFilters && <InlineFilters {...newFilters} {...filtersState} />}

      <Row style={{ position: 'relative' }} {...mainContentSettings}>
        {match('WAITING_FOR_DESTINATION') ||
        !isOriginSelected ||
        !isDestinationSelected ||
        match('ERROR') ? (
          <Col md="12">
            {renderFareCalendar({
              data: [],
              initialView: !isOriginSelected || !isDestinationSelected,
              errorStatus: match('ERROR')
            })}
          </Col>
        ) : (
          match('COMPLETE') && (
            <Col md="12">
              {renderFareCalendar({ data: fares.data, initialView: false, errorStatus: false })}
            </Col>
          )
        )}
      </Row>
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer floatLeft labelKey="cmp-fare-finder-calendar-disclaimer" {...disclaimer} />
          </Col>
        </Row>
      )}
    </Box>
  );
}

LowFareCalendarOIV2Container.propTypes = {
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if t container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Destination Component
   */
  destination: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * fareCalendar Component props
   */
  fareCalendar: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Handle the action when click on card
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistogramsSmart: PropTypes.func,
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Used to prepopulate the component
   */
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * legend Component props
   */
  legend: PropTypes.objectOf(PropTypes.any),
  /**
   * legend content settings
   */
  legendContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  origin: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * showFilters
   */
  showFilters: PropTypes.bool,
  /**
   * Show exchange button or not
   */
  showSwitch: PropTypes.bool,
  /**
   * Determine if the component will show the travelClass filter
   */
  showTravelClassFilter: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * showLegend
   */
  showLegend: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SwitchOriginDestination Component
   */
  switchOriginDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * travelClass options
   */
  travelClassOptions: PropTypes.objectOf(PropTypes.any),
  /**
   * travelClass of the component
   */
  travelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Metadata configuration
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * thresholdDay indicate past how many days the low fare calendar must display the upcoming month
   */
  thresholdDay: PropTypes.number
};
LowFareCalendarOIV2Container.defaultProps = {
  headerContentSettings: {},
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  containerType: 'stateful',
  destination: {},
  direction: 'ltr',
  disclaimer: {
    fontSize: '3'
  },
  disclaimerContentSettings: {
    marginTop: '2'
  },
  fareCalendar: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  filterContentSettings: {
    marginTop: '4'
  },
  filters: {},
  getAirports,
  getHistogramsSmart,
  minInitialResults: null,
  initialHistograms: null,
  integrationSettings: {},
  legend: {},
  legendContentSettings: {
    marginTop: '2'
  },
  loading: {},
  mainContentSettings: {
    marginTop: '4'
  },
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  origin: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showSwitch: true,
  showTravelClassFilter: false,
  showSkeletonScreen: true,
  showLegend: true,
  showLoading: true,
  showTitle: true,
  switchOriginDestination: {
    marginWidth: '1',
    borderColor: 'white',
    color: 'none',
    bgColor: 'transparent',
    style: { lineHeight: '0' },
    iconSettings: {
      animation: 'mirror',
      size: 20,
      style: {
        stroke: '#212529',
        strokeWidth: 50,
        width: 23
      }
    }
  },
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingBottom: '1.5rem' }
  },
  title: {},
  titleKey: 'cmp-low-calendar-container-title',
  translate: () => {},
  travelClassOptions: {
    options: [
      { label: 'economy', value: 'ECONOMY' },
      { label: 'business', value: 'BUSINESS' },
      { label: 'first', value: 'FIRST' }
    ]
  },
  travelClass: {},
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListLowFareCalendarOI'
  },
  localizationData: null,
  theme: null,
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-low-fare-calendar-sub-title',
  subTitleSettings: {},
  thresholdDay: 0
};
LowFareCalendarOIV2Container.componentName = 'LowFareCalendarOIV2Container';

// HOC with common functionality to all containers
export default withCommonEMContainer(LowFareCalendarOIV2Container);
